import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import { FONT_OPTIONS, TYPOGRAPHY_PRESETS, findFont } from './typography.system';
import { useTypography } from './TypographyProvider';

const titleSamples = [
  {
    name: 'Hero title',
    className: 'type-title-hero',
    sample: 'Life drawing, from wherever you are.',
    use: 'Landing heroes and immersive marketing screens.',
  },
  {
    name: 'Page title',
    className: 'type-title-page',
    sample: 'Create a drawing session',
    use: 'Top-level product, profile, and dashboard pages.',
  },
  {
    name: 'Section title',
    className: 'type-title-section',
    sample: 'Upcoming sessions',
    use: 'Major content groups inside a page.',
  },
  {
    name: 'Card title',
    className: 'type-title-card',
    sample: 'Sunday long pose',
    use: 'Cards, compact panels, and repeated items.',
  },
  {
    name: 'Modal title',
    className: 'type-title-modal',
    sample: 'Build pose schedule',
    use: 'Dialog headers and focused task surfaces.',
  },
];

const textSamples = [
  {
    name: 'Lede',
    className: 'type-body-lg',
    sample: 'Live, timed figure-drawing sessions with real models and artists.',
  },
  {
    name: 'Body',
    className: 'type-body',
    sample: 'Use this for the main explanatory text on public pages and product surfaces.',
  },
  {
    name: 'Small',
    className: 'type-body-sm',
    sample: 'Secondary context, help text, and dense dashboard detail.',
  },
  {
    name: 'Caption',
    className: 'type-caption',
    sample: 'Photo credit, timestamps, counters, and unobtrusive metadata.',
  },
  {
    name: 'Label',
    className: 'type-label',
    sample: 'Clear field labels and compact row headings.',
  },
  {
    name: 'Button',
    className: 'type-button',
    sample: 'Action label',
  },
  {
    name: 'Kicker',
    className: 'type-kicker',
    sample: 'From the sessions',
  },
  {
    name: 'Validation',
    className: 'type-validation-message',
    sample: 'Choose a model or enter an email before continuing.',
  },
];

const auditRows = [
  ['Titles', 'h1-h6, font-display, page-title, section-title, form-heading, hero h1, Wordmark', 'Fraunces only, weight 300, with panel-controlled letter spacing.'],
  ['Regular copy', 'body, paragraphs, labels, inline text, inputs', 'Mapped to --font-body; Literata is the default.'],
  ['Text links', 'Inline help, secondary navigation, and contextual references', 'Use text-link: underlined by default, with stronger decoration on hover and visible focus.'],
  ['Buttons', 'btn, btn-primary, btn-accent, btn-ghost, form-btn, marketing CTAs', 'Mapped to --font-button; Literata is the default.'],
  ['Utility labels', 'kicker, eyebrow, form-eyebrow, badges, counters, schedule metadata', 'Mapped to --font-mono.'],
  ['Validation', 'Inline form errors and submission blockers', 'Use type-validation-message so errors are prominent and consistent.'],
  ['Legacy typography utilities', 'font-display, font-sans, font-mono, form styles, hero styles', 'Caught by the central typography stylesheet.'],
];

export function TypographyAuditPage() {
  const { settings } = useTypography();

  return (
    <div className="min-h-screen bg-bg text-text">
      <NavBar variant="light" links={[
        { label: 'Drawing Events', href: '/events' },
        { label: 'Artists', href: '/artists' },
        { label: 'Models', href: '/models' },
        { label: 'Typography', href: '/typography', active: true },
      ]} />

      <main className="mx-auto flex w-full max-w-[1240px] flex-col gap-12 px-6 pb-24 pt-32 md:px-10">
        <header className="grid gap-8 border-b border-line pb-12 lg:grid-cols-[1fr_360px]">
          <div>
            <p className="type-kicker mb-5 text-text-dim">Site typography audit</p>
            <h1 className="type-title-hero max-w-[11ch]">One type system, live controls.</h1>
            <p className="type-body-lg mt-8 max-w-[680px] text-text-dim">
              This page standardizes the site around title types, primary and secondary buttons, and a compact set of regular text styles. Use the panel in the lower-right corner to change the active font mix across the app.
            </p>
          </div>

          <section className="self-start rounded-xl border border-line bg-panel p-5 shadow-md">
            <p className="type-kicker text-text-faint">Current fonts</p>
            <div className="mt-4 grid gap-3">
              <ActiveFont label="Titles" fontId="fraunces" />
              <ActiveValue label="Title spacing" value={`${settings.titleLetterSpacing.toFixed(3)}em`} />
              <ActiveFont label="Regular text" fontId={settings.bodyFontId} />
              <ActiveValue label="Copy spacing" value={`${settings.bodyLetterSpacing.toFixed(3)}em`} />
              <ActiveValue label="Copy weight" value={String(settings.bodyFontWeight)} />
              <ActiveValue label="Copy size" value={`${settings.bodyFontSize}px`} />
              <ActiveFont label="Buttons" fontId={settings.buttonFontId} />
              <ActiveFont label="Utility labels" fontId={settings.monoFontId} />
            </div>
          </section>
        </header>

        <section>
          <div className="mb-6 flex flex-col justify-between gap-3 md:flex-row md:items-end">
            <div>
              <p className="type-kicker text-text-faint">Standard title types</p>
              <h2 className="type-title-section mt-2">Titles</h2>
            </div>
            <p className="type-body-sm max-w-[440px] text-text-dim">
              These map to reusable classes and the existing display styles, so changing the title font updates the broad page language.
            </p>
          </div>

          <div className="grid gap-4">
            {titleSamples.map((sample) => (
              <article key={sample.name} className="rounded-xl border border-line bg-panel p-5 shadow-sm">
                <div className="mb-4 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                  <h3 className="type-label text-text">{sample.name}</h3>
                  <span className="type-code text-text-faint">{sample.className}</span>
                </div>
                <p className={sample.className}>{sample.sample}</p>
                <p className="type-body-sm mt-4 max-w-[620px] text-text-dim">{sample.use}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="grid gap-8 lg:grid-cols-[1fr_420px]">
          <div>
            <p className="type-kicker text-text-faint">Buttons</p>
            <h2 className="type-title-section mt-2">Primary and secondary actions</h2>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" className="btn btn-primary btn-lg">Primary button</button>
              <button type="button" className="btn btn-secondary btn-lg">Secondary button</button>
              <button type="button" className="btn btn-ghost btn-lg">Quiet button</button>
            </div>
            <div className="mt-6 rounded-xl border border-line bg-panel p-5">
              <div className="flex flex-wrap gap-3">
                <button type="button" className="btn btn-accent">Accent action</button>
                <button type="button" className="btn">Default action</button>
                <button type="button" className="btn btn-sm">Small action</button>
              </div>
            </div>
          </div>

          <div>
            <p className="type-kicker text-text-faint">Regular text</p>
            <h2 className="type-title-section mt-2">Limited text set</h2>
            <div className="mt-6 grid gap-3">
              {textSamples.map((sample) => (
                <article key={sample.name} className="rounded-lg border border-line bg-panel p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="type-label-sm text-text">{sample.name}</h3>
                    <span className="type-code text-text-faint">{sample.className}</span>
                  </div>
                  <p className={sample.className}>{sample.sample}</p>
                </article>
              ))}
              <article className="rounded-lg border border-line bg-panel p-4">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="type-label-sm text-text">Text link</h3>
                  <span className="type-code text-text-faint">text-link</span>
                </div>
                <p className="type-body-sm text-text-dim">
                  Use inline links for quiet secondary actions, like{' '}
                  <a href="#text-link-sample" className="text-link">
                    opening a guide
                  </a>
                  .
                </p>
              </article>
            </div>
          </div>
        </section>

        <section>
          <p className="type-kicker text-text-faint">Audit map</p>
          <h2 className="type-title-section mt-2">Where the existing site lands</h2>
          <div className="mt-6 overflow-hidden rounded-xl border border-line bg-panel shadow-sm">
            <table className="w-full min-w-[760px] text-left">
              <thead className="border-b border-line bg-bg-secondary">
                <tr>
                  <th className="type-label-sm px-4 py-3 text-text-dim">Role</th>
                  <th className="type-label-sm px-4 py-3 text-text-dim">Current usage</th>
                  <th className="type-label-sm px-4 py-3 text-text-dim">Standard</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map(([role, usage, standard]) => (
                  <tr key={role} className="border-b border-line-soft last:border-b-0">
                    <td className="type-label px-4 py-4 text-text">{role}</td>
                    <td className="px-4 py-4 type-body-sm text-text-dim">{usage}</td>
                    <td className="px-4 py-4 type-body-sm text-text-dim">{standard}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <p className="type-kicker text-text-faint">Available styles</p>
          <h2 className="type-title-section mt-2">Font library and presets</h2>
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-line bg-panel p-5">
              <h3 className="type-title-card">Presets</h3>
              <div className="mt-4 grid gap-3">
                {TYPOGRAPHY_PRESETS.map((preset) => (
                  <div key={preset.id} className="border-b border-line-soft pb-3 last:border-b-0 last:pb-0">
                    <div className="type-label text-text">{preset.label}</div>
                    <p className="type-body-sm mt-1 text-text-dim">{preset.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-line bg-panel p-5">
              <h3 className="type-title-card">Fonts</h3>
              <div className="mt-4 grid gap-3">
                {FONT_OPTIONS.map((font) => (
                  <div key={font.id} className="border-b border-line-soft pb-3 last:border-b-0 last:pb-0">
                    <div className="type-label text-text" style={{ fontFamily: font.stack, fontWeight: font.id === 'fraunces' ? 300 : undefined }}>{font.label}</div>
                    <p className="type-body-sm mt-1 text-text-dim">{font.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <SharedFooter />
    </div>
  );
}

function ActiveFont({ label, fontId }: { label: string; fontId: string }) {
  const font = findFont(fontId);

  return (
    <div className="rounded-lg border border-line-soft bg-bg-secondary p-3">
      <div className="type-kicker text-text-faint">{label}</div>
      <div className="type-body-lg mt-1 text-text" style={{ fontFamily: font.stack, fontWeight: font.id === 'fraunces' ? 300 : undefined }}>{font.label}</div>
    </div>
  );
}

function ActiveValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-line-soft bg-bg-secondary p-3">
      <div className="type-kicker text-text-faint">{label}</div>
      <div className="type-body-lg mt-1 text-text">{value}</div>
    </div>
  );
}
